import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Mail, Phone, ArrowRight, ShieldCheck, RefreshCw } from "lucide-react";

/**
 * VerifyCodeDialog
 * Two-step flow for verifying email or phone:
 *   1. Enter target (email/phone) → we send a 6-digit code
 *   2. Enter code → server confirms and flips the verification bit
 *
 * Props:
 *   type: "email" | "phone"
 *   defaultTarget: initial value for the target input (usually the account email)
 *   open: boolean
 *   onOpenChange(open): dialog controller
 *   onVerified(): called after successful confirm — parent should refresh profile
 */
export default function VerifyCodeDialog({ type = "email", defaultTarget = "", open, onOpenChange, onVerified }) {
  const [step, setStep] = useState(1);          // 1 = enter target, 2 = enter code
  const [target, setTarget] = useState(defaultTarget || "");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null); // shown when Resend isn't configured
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resending, setResending] = useState(false);

  const isEmail = type === "email";
  const Icon = isEmail ? Mail : Phone;
  const label = isEmail ? "email" : "phone";

  // Reset target whenever the dialog is (re)opened OR the type changes,
  // so switching from email → phone doesn't leave the previous email in the field.
  useEffect(() => {
    if (open) {
      setTarget(defaultTarget || "");
      setStep(1);
      setCode("");
      setDevCode(null);
    }
  }, [open, type, defaultTarget]);

  const reset = () => {
    setStep(1);
    setCode("");
    setDevCode(null);
    setSending(false);
    setConfirming(false);
    setResending(false);
  };

  const startFlow = async () => {
    setSending(true);
    try {
      const body = isEmail ? (target ? { email: target } : {}) : { phone: target };
      const { data } = await api.post(`/creators/me/verify/${type}/start`, body);
      setDevCode(data.dev_code || null);
      setStep(2);
      toast.success(
        data.delivered
          ? `Code sent to ${data.target}`
          : `Code generated for ${data.target}${data.dev_code ? " · shown below" : ""}`
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const body = isEmail ? { email: target } : { phone: target };
      const { data } = await api.post(`/creators/me/verify/${type}/start`, body);
      setDevCode(data.dev_code || null);
      toast.success(data.delivered ? "New code sent" : "New code generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  const confirmCode = async () => {
    setConfirming(true);
    try {
      await api.post(`/creators/me/verify/${type}/confirm`, { code: code.trim() });
      toast.success(`${label[0].toUpperCase()}${label.slice(1)} verified · +25 XP`);
      onVerified?.();
      onOpenChange?.(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange?.(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="bg-[#0C0C10] border-white/10 text-white max-w-md" data-testid={`verify-${type}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl nexus-gradient flex items-center justify-center">
              <Icon className="h-4 w-4 text-white" />
            </div>
            <span>Verify your {label}</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              {isEmail
                ? "We&apos;ll email you a 6-digit code. Enter it on the next step to confirm this address."
                : "We&apos;ll send a 6-digit code by SMS. (Note: SMS delivery not yet wired — code shown here for now.)"}
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">{isEmail ? "Email address" : "Phone number"}</Label>
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value.trim())}
                placeholder={isEmail ? "you@example.com" : "+44 7000 000000"}
                className="mt-1.5 bg-white/[0.02] border-white/10 text-white"
                data-testid={`verify-${type}-target-input`}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                onClick={startFlow}
                disabled={sending || !target}
                className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full h-11"
                data-testid={`verify-${type}-send-btn`}
              >
                {sending ? "Sending…" : (<>Send code <ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Enter the 6-digit code we {isEmail ? "just emailed to" : "sent to"} <span className="text-white font-medium">{target}</span>.
            </p>

            {devCode && (
              <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 p-3 text-xs text-yellow-200 space-y-1">
                <div className="uppercase tracking-widest text-[10px] font-bold">Dev mode · {isEmail ? "Resend not configured" : "SMS not wired yet"}</div>
                <div className="font-mono text-lg tracking-widest text-white" data-testid={`verify-${type}-dev-code`}>{devCode}</div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">6-digit code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                className="mt-1.5 bg-white/[0.02] border-white/10 text-white text-center text-2xl font-mono tracking-[0.5em]"
                data-testid={`verify-${type}-code-input`}
                autoFocus
              />
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                className="text-xs text-nexus-blue hover:underline inline-flex items-center gap-1"
                data-testid={`verify-${type}-resend-btn`}
              >
                <RefreshCw className={`h-3 w-3 ${resending ? "animate-spin" : ""}`} /> {resending ? "Sending…" : "Resend code"}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Change {label}
              </button>
            </div>

            <DialogFooter>
              <Button
                onClick={confirmCode}
                disabled={confirming || code.length !== 6}
                className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full h-11"
                data-testid={`verify-${type}-confirm-btn`}
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                {confirming ? "Verifying…" : "Confirm"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
